// Local persistence of a space. One interface, two implementations: IndexedDB in the browser,
// memory in unit tests. Everything here is local to the device; content stays plaintext in the
// local replica (same threat model as notd: server confidentiality, not device theft).

import type { Envelope } from './envelope';

export type StoredUpdate = { note: string; author: string; seq: number; ts: number; update: Uint8Array };

export interface Store {
  getKv<T>(key: string): Promise<T | undefined>;
  setKv(key: string, value: unknown): Promise<void>;
  putEnvelope(e: Envelope): Promise<void>;
  /** Envelopes of one author with seq >= fromSeq, in order. */
  envelopesFrom(author: string, fromSeq: number): Promise<Envelope[]>;
  putUpdate(u: StoredUpdate): Promise<void>;
  updatesFor(note: string): Promise<StoredUpdate[]>;
  putDoc(note: string, state: Uint8Array): Promise<void>;
  getDoc(note: string): Promise<Uint8Array | undefined>;
  putIndex(note: string, meta: unknown): Promise<void>;
  allIndex<T>(): Promise<T[]>;
  /** Removes everything (leaving a space). */
  clear(): Promise<void>;
}

export class MemoryStore implements Store {
  kv = new Map<string, unknown>();
  envelopes = new Map<string, Envelope[]>();
  updates: StoredUpdate[] = [];
  docs = new Map<string, Uint8Array>();
  index = new Map<string, unknown>();

  // Values are kept by reference: CryptoKey handles are not cloneable in every runtime.
  async getKv<T>(key: string) {
    return this.kv.get(key) as T | undefined;
  }
  async setKv(key: string, value: unknown) {
    this.kv.set(key, value);
  }
  async putEnvelope(e: Envelope) {
    const list = this.envelopes.get(e.author) ?? [];
    list[e.seq - 1] = e;
    this.envelopes.set(e.author, list);
  }
  async envelopesFrom(author: string, fromSeq: number) {
    return (this.envelopes.get(author) ?? []).slice(fromSeq - 1).filter(Boolean);
  }
  async putUpdate(u: StoredUpdate) {
    this.updates.push(u);
  }
  async updatesFor(note: string) {
    return this.updates.filter((u) => u.note === note).sort((a, b) => a.ts - b.ts);
  }
  async putDoc(note: string, state: Uint8Array) {
    this.docs.set(note, state);
  }
  async getDoc(note: string) {
    return this.docs.get(note);
  }
  async putIndex(note: string, meta: unknown) {
    this.index.set(note, structuredClone(meta));
  }
  async allIndex<T>() {
    return [...this.index.values()] as T[];
  }
  async clear() {
    this.kv.clear();
    this.envelopes.clear();
    this.updates = [];
    this.docs.clear();
    this.index.clear();
  }
}

// ---- IndexedDB ----

const DB_NAME = 'space-v1';
const STORES = ['kv', 'envelopes', 'updates', 'docs', 'index'] as const;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export class IdbStore implements Store {
  #db: Promise<IDBDatabase>;

  constructor(name = DB_NAME) {
    this.#db = new Promise((resolve, reject) => {
      const open = indexedDB.open(name, 1);
      open.onupgradeneeded = () => {
        const db = open.result;
        db.createObjectStore('kv');
        db.createObjectStore('envelopes', { keyPath: ['author', 'seq'] });
        db.createObjectStore('updates', { autoIncrement: true }).createIndex('note', 'note');
        db.createObjectStore('docs');
        db.createObjectStore('index');
      };
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => reject(open.error);
    });
  }

  async #tx<T>(store: (typeof STORES)[number], mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.#db;
    return req(fn(db.transaction(store, mode).objectStore(store)));
  }

  getKv<T>(key: string) {
    return this.#tx('kv', 'readonly', (s) => s.get(key)) as Promise<T | undefined>;
  }
  async setKv(key: string, value: unknown) {
    await this.#tx('kv', 'readwrite', (s) => s.put(value, key));
  }
  async putEnvelope(e: Envelope) {
    await this.#tx('envelopes', 'readwrite', (s) => s.put(e));
  }
  envelopesFrom(author: string, fromSeq: number) {
    return this.#tx('envelopes', 'readonly', (s) =>
      s.getAll(IDBKeyRange.bound([author, fromSeq], [author, Number.MAX_SAFE_INTEGER])),
    ) as Promise<Envelope[]>;
  }
  async putUpdate(u: StoredUpdate) {
    await this.#tx('updates', 'readwrite', (s) => s.add(u));
  }
  async updatesFor(note: string) {
    const all = (await this.#tx('updates', 'readonly', (s) => s.index('note').getAll(note))) as StoredUpdate[];
    return all.sort((a, b) => a.ts - b.ts);
  }
  async putDoc(note: string, state: Uint8Array) {
    await this.#tx('docs', 'readwrite', (s) => s.put(state, note));
  }
  getDoc(note: string) {
    return this.#tx('docs', 'readonly', (s) => s.get(note)) as Promise<Uint8Array | undefined>;
  }
  async putIndex(note: string, meta: unknown) {
    await this.#tx('index', 'readwrite', (s) => s.put(meta, note));
  }
  allIndex<T>() {
    return this.#tx('index', 'readonly', (s) => s.getAll()) as Promise<T[]>;
  }
  async clear() {
    const db = await this.#db;
    const tx = db.transaction([...STORES], 'readwrite');
    for (const name of STORES) tx.objectStore(name).clear();
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}
